/**
 * <bundle-selector>
 *
 * Manages all bundle-selector logic: variant selection, price rendering, colour availability,
 * OOS auto-select, Swiper carousel, tab/slider toggle, ATC with SG mini-cart integration,
 * and browser URL sync.
 *
 * Data sources (read on connectedCallback + DOMContentLoaded):
 *   #product       — #pdp-product-json        (child of this element)
 *   #hideFlags     — #variant-hide-metafield   (child of this element)
 *   #badgeFlags    — #variant-bundle-badge     (child of this element)
 *   #settings      — [data-jsc-bundle-config]  (child of this element)
 *   #bundleContentMap — included inside [data-jsc-bundle-config] as bundleContentMap
 *   #moneyFormat   — data-money-format attribute on this element
 *
 * Requires: window.Swiper (Swiper 8, loaded before this script via swiper-bundle.min.js)
 */
(function () {
  const ROOT = '.art-water-maker-variants'
  const COLOR = '.art-water-maker-variants__color-variant'
  const DEAL = '.art-water-maker-variants__deal-variant'
  const TABS = 'art-water-maker-variants__variant-tabs'
  const SLIDER = 'art-water-maker-variants__variant-slider'
  const BUNDLE_ITEM = 'art-water-maker-variants__bundle-item'
  const BUNDLE_ITEM_OOS = 'art-water-maker-variants__bundle-item--out-of-stock'
  const BUNDLE_ITEM_NOT_EXIST = 'art-water-maker-variants__bundle-item--not-exist'
  const BUNDLE_ITEM_BADGE = 'art-water-maker-variants__bundle-item--has-badge'
  const SINGLE = 'art-water-maker-variants__deal-variant--single-bundle'
  const PRICE = '.art-water-maker-variants__bundle-price'
  const PRICE_COMPARE = '.art-water-maker-variants__bundle-price--compare'
  const SLIDER_CONTENT = '.art-water-maker-variants__slider-bundle-content'
  const TAB_CONTENT = '.art-water-maker-variants__tab-bundle-content'
  const TAB_CONTENT_OOS = 'art-water-maker-variants__tab-bundle-content--out-of-stock'
  const ATC = '.art-water-maker-variants__atc'
  const BUNDLE_NAME = '.art-water-maker-variants__bundle-name'
  const FORM_SELECT = 'select.art-water-maker-variants__product-form-selector'
  const PAGINATION = '.art-water-maker-variants__bundles-pagination'
  const PREV = '.art-water-maker-variants__bundle-prev'
  const NEXT = '.art-water-maker-variants__bundle-next'
  const CROSSED_OUT = 'variant-option--crossed-out'
  const HOVER_ON = 'variant-option--hover'
  const HIDDEN = 'variant-option--hidden'
  const MOBILE_MAX = 990

  class BundleSelector extends HTMLElement {
    // ── Private state ────────────────────────────────────────────────────
    #product      = null   // full Shopify product object
    #hideFlags    = {}     // { variantId: true/false }
    #badgeFlags   = {}     // { variantId: true/false } from custom.bundle_badge
    #settings     = {}     // from data-jsc-bundle-config
    #bundleContentMap = {}  // { variantId: htmlString } from custom.bundle_description_html metafield
    #currentVariant = null // currently selected variant object (or null)
    #swiper       = null   // Swiper instance for bundle cards carousel
    #isTabMode    = false  // true → tab layout; false → swiper carousel
    #isMobile     = false  // true if viewport < 990 px
    #singleBundle = false  // true if only one deal option exists
    #moneyFormat  = ''     // Shopify money format string, e.g. "${{amount}}"
    #colorIdx     = 0      // index of "Color" in product.options
    #dealIdx      = 1      // index of "Deal" in product.options

    // ── Lifecycle ────────────────────────────────────────────────────────
    connectedCallback() {
      this.#readConfig()

      const init = () => {
        this.#safeStep('detectEnvironment',     () => this.#detectEnvironment())
        this.#safeStep('attachListeners',       () => this.#attachListeners())
        this.#safeStep('disableUnusableColors', () => this.#disableUnusableColors())
        this.#safeStep('syncVariant',           () => this.#syncVariant())
        this.#safeStep('updateColorCrossedOut', () => this.#updateColorCrossedOut())
        this.#safeStep('refreshBundleCards',    () => this.#refreshBundleCards())
        this.#safeStep('updateAtcButton',       () => this.#updateAtcButton())
        this.#safeStep('updateUrl',             () => this.#updateUrl())
        if (this.#isTabMode) this.#safeStep('syncTabContent', () => this.#syncTabContent())
        this.#safeStep('autoSelectIfOos', () => this.#autoSelectIfOos())
        if (!this.#isTabMode && !this.#singleBundle) {
          this.#safeStep('initSwiper', () => this.#initSwiper())
        }
        this.#safeStep('initColorSwatches', () => this.#initColorSwatches())
      }

      if (document.readyState !== 'complete') {
        document.addEventListener('DOMContentLoaded', init, { once: true })
      } else {
        init()
      }
    }

    /** Run a lifecycle step in isolation so one throwing step can't skip the rest (e.g. Swiper never getting initialised). */
    #safeStep(name, fn) {
      try {
        fn()
      } catch (e) {
        console.error(`[bundle-selector] step "${name}" threw — continuing init:`, e)
      }
    }

    // ── Data reading ─────────────────────────────────────────────────────
    #readConfig() {
      try {
        const productEl = this.querySelector('#pdp-product-json')
        if (productEl) this.#product = JSON.parse(productEl.textContent)

        const hideEl = this.querySelector('#variant-hide-metafield')
        if (hideEl) this.#hideFlags = JSON.parse(hideEl.textContent)

        const badgeEl = this.querySelector('#variant-bundle-badge')
        if (badgeEl) this.#badgeFlags = JSON.parse(badgeEl.textContent)

        const configEl = this.querySelector('[data-jsc-bundle-config]')
        if (configEl) {
          const cfg = JSON.parse(configEl.textContent)
          this.#settings        = cfg
          this.#bundleContentMap = cfg.bundleContentMap ?? {}
        }

        this.#moneyFormat = this.dataset.moneyFormat ?? ''

        if (this.#product) {
          const opts = this.#product.options ?? []
          const ci = opts.indexOf('Color')
          const di = opts.indexOf('Deal')
          this.#colorIdx = ci >= 0 ? ci : 0
          this.#dealIdx  = di >= 0 ? di : 1
        }
      } catch (e) {
        console.error('[bundle-selector] Failed to read config:', e)
      }
    }

    // ── Environment detection ─────────────────────────────────────────────
    #detectEnvironment() {
      this.#isMobile = window.innerWidth < MOBILE_MAX

      const labels = [...this.querySelectorAll(`${DEAL} label`)]
      this.#singleBundle = labels.length === 1
      if (this.#singleBundle) {
        this.querySelector(DEAL)?.classList.add(SINGLE)
      }

      const tabs = this.querySelector(`${DEAL}.${TABS}`)
      if (tabs) {
        const visibleCount = labels.filter(l => l.getAttribute('hide-storefront') !== 'true').length
        if (visibleCount > 3 || this.#isMobile) {
          tabs.classList.remove(TABS)
          tabs.classList.add(SLIDER)
          this.#isTabMode = false
        } else {
          this.#isTabMode = true
        }
      } else {
        this.#isTabMode = false
      }
    }

    // ── Core helpers ──────────────────────────────────────────────────────

    /** Find a variant matching all provided option values (by position). */
    #findVariant(optionsArray) {
      if (!this.#product) return null
      return this.#product.variants.find(v =>
        optionsArray.every((val, i) => v.options[i] === val)
      ) ?? null
    }

    /** Return true if this variant should be hidden from the storefront. */
    #isHidden(variantId) {
      const v = this.#hideFlags[variantId]
      return v === true || v === 'true' || v === 1 || v === '1'
    }

    /** Return true if this variant's pack should show the theme badge image. */
    #hasBadge(variantId) {
      const v = this.#badgeFlags[variantId]
      return v === true || v === 'true'
    }

    /** Format a price in cents using the theme currency formatter. */
    #formatMoney(cents) {
      if (!cents && cents !== 0) return ''
      if (window.theme?.Currency?.formatMoney) {
        return window.theme.Currency.formatMoney(cents, this.#moneyFormat)
      }
      // Fallback for environments where theme.Currency isn't loaded yet
      return '$' + (cents / 100).toFixed(2)
    }

    #isOos(variant) {
      if (!variant) return true
      return !variant.available || this.#isHidden(variant.id)
    }

    // ── Bundle card rendering (A) ─────────────────────────────────────────
    #refreshBundleCards() {
      const colorInput = this.querySelector(`${COLOR} input:checked`)
      const dealInputs = [...this.querySelectorAll(`${DEAL} input[type="radio"]`)]

      dealInputs.forEach(input => {
        const label = this.querySelector(`label[for="${input.id}"]`)
        if (!label) return

        const dealValue    = input.value
        const selectedColor = colorInput?.value ?? ''
        const variant       = selectedColor ? this.#findVariant([selectedColor, dealValue]) : null

        if (!variant) {
          label.classList.add(BUNDLE_ITEM_NOT_EXIST)
          label.classList.remove('swiper-slide', BUNDLE_ITEM_BADGE, BUNDLE_ITEM_OOS)
          const priceEl = label.querySelector(PRICE)
          if (priceEl) priceEl.textContent = ''
          return
        }

        label.classList.remove(BUNDLE_ITEM_NOT_EXIST)
        label.classList.add('swiper-slide')

        const content = label.querySelector(SLIDER_CONTENT)
        if (content) {
          const mapContent = this.#bundleContentMap[variant.id]
          if (mapContent != null) content.innerHTML = mapContent
        }

        const priceEl    = label.querySelector(PRICE)
        const oldPriceEl = label.querySelector(PRICE_COMPARE)
        if (priceEl) priceEl.innerHTML = this.#formatMoney(variant.price)
        if (oldPriceEl) {
          oldPriceEl.innerHTML = variant.compare_at_price > 0
            ? this.#formatMoney(variant.compare_at_price)
            : ''
        }

        const hidden = this.#isHidden(variant.id)
        label.setAttribute('hide-storefront', hidden ? 'true' : 'false')
        if (hidden) input.disabled = true

        label.classList.toggle(BUNDLE_ITEM_BADGE, this.#hasBadge(variant.id))
        label.classList.toggle(BUNDLE_ITEM_OOS, this.#isOos(variant))
      })

      this.#swiper?.update()
    }

    // ── Variant selection (M) ─────────────────────────────────────────────
    #syncVariant() {
      const colorInput = this.querySelector(`${COLOR} input:checked`)
      const dealInput  = this.querySelector(`${DEAL} input:checked`)

      let variant = null
      if (colorInput && dealInput) {
        variant = this.#findVariant([colorInput.value, dealInput.value])
        if (variant && this.#isHidden(variant.id)) variant = null
      }

      if (variant) {
        this.#currentVariant = variant
        const sel = this.querySelector(FORM_SELECT)
        if (sel) sel.value = variant.id
        document.dispatchEvent(new CustomEvent('theme:variant:change', {
          bubbles: true,
          detail: { variant }
        }))

        if (variant.featured_media?.id) {
          const section = this.closest('[data-section-id]')
          const slideshow = section?.querySelector('[data-product-slideshow]')
          if (slideshow) {
            slideshow.dispatchEvent(new CustomEvent('theme:image:change', {
              detail: { id: variant.featured_media.id }
            }))
          }
        }
      } else {
        const fallback = this.#product?.variants.find(v => v.available && !this.#isHidden(v.id))
        if (fallback) this.#autoClickVariant(fallback)
      }
    }

    /** Re-run variant sync + dependent UI updates after the checked radio changes (click or Swiper-driven). */
    #applyVariantChange() {
      this.#syncVariant()
      this.#updateColorCrossedOut()
      this.#updateAtcButton()
      this.#updateUrl()
    }

    // ── Colour crossed-out state (j) ──────────────────────────────────────
    #updateColorCrossedOut() {
      const dealInput  = this.querySelector(`${DEAL} input:checked`)
      const dealValue  = dealInput?.value ?? ''
      const colorInputs = [...this.querySelectorAll(`${COLOR} input[type="radio"]`)]

      colorInputs.forEach(input => {
        const wrapper = input.closest('.variant-option')
        if (!wrapper) return

        const variant = this.#findVariant([input.value, dealValue])
        if (!variant || !variant.available || this.#isHidden(variant.id)) {
          wrapper.classList.add(CROSSED_OUT)
        } else {
          wrapper.classList.remove(CROSSED_OUT)
        }
      })
    }

    // ── Disable colours with no valid bundles (C) ─────────────────────────
    #disableUnusableColors() {
      const dealValues  = [...this.querySelectorAll(`${DEAL} input[type="radio"]`)]
        .map(i => i.value)
      const colorInputs = [...this.querySelectorAll(`${COLOR} input[type="radio"]`)]

      colorInputs.forEach(input => {
        const hasAnyDeal = dealValues.some(deal => {
          const v = this.#findVariant([input.value, deal])
          return v && v.available && !this.#isHidden(v.id)
        })
        const wrapper = input.closest('.variant-option')
        if (!hasAnyDeal) {
          input.disabled = true
          wrapper?.classList.add(HIDDEN)
        }
      })
    }

    // ── ATC button state (O) ──────────────────────────────────────────────
    #updateAtcButton() {
      const btn     = this.querySelector(ATC)
      const nameEl  = this.querySelector(BUNDLE_NAME)
      const checked = this.querySelector(`${DEAL} input:checked`)

      if (nameEl && checked) nameEl.textContent = checked.value

      if (btn) {
        const available = this.#currentVariant?.available === true
          && !this.#isHidden(this.#currentVariant?.id)
        btn.disabled = !available
      }
    }

    // ── URL history (I) ───────────────────────────────────────────────────
    #updateUrl() {
      if (!this.#currentVariant) return
      try {
        const url = new URL(window.location.href)
        url.searchParams.set('variant', this.#currentVariant.id)
        window.history.replaceState({}, '', url.toString())
      } catch (_) {}
    }

    // ── Tab content sync (T) ──────────────────────────────────────────────
    #syncTabContent() {
      const tabView    = this.querySelector(TAB_CONTENT)
      const checked    = this.querySelector(`${DEAL} input:checked`)
      if (!tabView || !checked) return

      const label         = this.querySelector(`label[for="${checked.id}"]`)
      const sliderContent = label?.querySelector(SLIDER_CONTENT)
      tabView.innerHTML   = sliderContent?.innerHTML ?? ''

      const isOos = this.#isOos(this.#currentVariant)
      label?.classList.toggle(BUNDLE_ITEM_OOS, isOos)
      tabView.classList.toggle(TAB_CONTENT_OOS, isOos)
    }

    // ── OOS auto-select ───────────────────────────────────────────────────
    #autoSelectIfOos() {
      if (this.#currentVariant?.available && !this.#isHidden(this.#currentVariant.id)) return
      if (!this.#product) return

      const colorInput = this.querySelector(`${COLOR} input:checked`)
      const dealInput  = this.querySelector(`${DEAL} input:checked`)
      const strategy   = this.#settings.out_of_stock_select ?? 'color'

      let alt = null
      if (strategy === 'bundle' && colorInput) {
        alt = this.#product.variants.find(v =>
          v.available && !this.#isHidden(v.id)
          && v.options[this.#colorIdx] === colorInput.value
        )
      } else if (dealInput) {
        alt = this.#product.variants.find(v =>
          v.available && !this.#isHidden(v.id)
          && v.options[this.#dealIdx] === dealInput.value
        )
      }

      alt ??= this.#product.variants.find(v => v.available && !this.#isHidden(v.id))
      if (alt) this.#autoClickVariant(alt)
    }

    // ── Programmatic variant selection (B) ────────────────────────────────
    #autoClickVariant(variant) {
      const colorId = `Option${this.#colorIdx + 1}-${variant.options[this.#colorIdx]}`
      const dealId  = `Option${this.#dealIdx  + 1}-${variant.options[this.#dealIdx]}`

      const colorInput = this.querySelector(`${COLOR} input[id="${colorId}"]`)
      const dealInput  = this.querySelector(`${DEAL} input[id="${dealId}"]`)

      colorInput?.click()
      dealInput?.click()

      if (this.#swiper && dealInput) {
        const pos = parseInt(dealInput.dataset.valuePosition ?? '0', 10)
        this.#swiper.slideTo(pos, 0)
      }
    }

    // ── Swiper (bundle cards carousel) ───────────────────────────────────
    #initSwiper() {
      if (!window.Swiper) {
        console.warn('[bundle-selector] window.Swiper not found — bundle carousel disabled')
        return
      }

      this.#swiper = new window.Swiper(
        `${ROOT} ${DEAL}`,
        {
          slidesPerView: 'auto',
          spaceBetween: 15,
          rewind: true,
          allowTouchMove: this.#isMobile,
          centeredSlides: true,
          centeredSlidesBounds: true,
          pagination: {
            el: `${ROOT} ${PAGINATION}`,
            type: 'bullets',
            clickable: true
          },
          navigation: {
            prevEl: `${ROOT} ${PREV}`,
            nextEl: `${ROOT} ${NEXT}`
          }
        }
      )

      this.#swiper.on('slideChange', swiper => {
        const slide = swiper.slides[swiper.activeIndex]
        const radio = slide?.previousElementSibling
        if (radio?.type === 'radio' && !radio.checked) {
          radio.checked = true
          this.#applyVariantChange()
        }
      })

      const checked = this.querySelector(`${DEAL} input:checked`)
      if (checked) {
        const pos = parseInt(checked.dataset.valuePosition ?? '0', 10)
        this.#swiper.slideTo(pos, 0)
      }
    }

    // ── Event listeners ───────────────────────────────────────────────────
    #attachListeners() {
      this.querySelectorAll(
        `${COLOR} input[type="radio"], ${DEAL} input[type="radio"]`
      ).forEach(input => {
        input.addEventListener('change', e => {
          const isColorChange = !!e.target.closest(COLOR)

          if (isColorChange) {
            this.#refreshBundleCards()
            if (this.#isTabMode) this.#syncTabContent()
          }

          this.#applyVariantChange()

          if (!isColorChange) {
            if (this.#isTabMode) this.#syncTabContent()
            if (this.#swiper) {
              const pos = parseInt(e.target.dataset.valuePosition ?? '0', 10)
              this.#swiper.slideTo(pos)
            }
          }
        })
      })

      this.querySelector(ATC)
        ?.addEventListener('click', e => this.#handleAtcClick(e))

      if (this.#isMobile) {
        this.querySelectorAll(`${COLOR} .variant-option`).forEach(wrapper => {
          let timer
          wrapper.addEventListener('touchstart', () => {
            if (!wrapper.classList.contains(CROSSED_OUT)) return
            wrapper.classList.add(HOVER_ON)
            clearTimeout(timer)
            timer = setTimeout(() => wrapper.classList.remove(HOVER_ON), 2000)
          }, { passive: true })
        })
      }
    }

    // ── Add to Cart ───────────────────────────────────────────────────────
    #handleAtcClick(event) {
      event.preventDefault()
      if (!this.#currentVariant) return

      const btn = event.currentTarget
      btn.disabled = true

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: this.#currentVariant.id,
          quantity: 1,
          properties: {}
        })
      })
        .then(r => r.ok ? fetch('/cart.js').then(r2 => r2.json()) : null)
        .then(cart => {
          if (cart) {
            document.dispatchEvent(new CustomEvent('theme:cart:change', {
              bubbles: true,
              detail: { cart }
            }))
            document.dispatchEvent(new CustomEvent('theme:cart:popdown', {
              bubbles: true,
              detail: { variant: { id: this.#currentVariant.id } }
            }))
          }
        })
        .catch(err => console.error('[bundle-selector] Cart add failed:', err))
        .finally(() => { btn.disabled = false })
    }

    // ── Color swatch initialisation ───────────────────────────────────────
    async #initColorSwatches() {
      const url = window.theme?.assets?.swatches;
      if (!url) return
      try {
        const { colors } = await fetch(url).then(r => r.json())
        const map = {}
        colors.forEach(obj => {
          const [name, val] = Object.entries(obj)[0]
          map[name.toLowerCase()] = val
        })
        this.querySelectorAll(`${COLOR} .swatch__button`).forEach(span => {
          const label = span.querySelector('label[data-swatch]')
          if (!label) return
          const hex = map[label.dataset.swatch.toLowerCase()]
          if (hex) span.style.setProperty('--swatch', hex)
        })
      } catch (_) {}
    }
  }

  customElements.define('bundle-selector', BundleSelector)
})()
